import { getLogger } from '@logtape/logtape';
import { randomId } from '@mantine/hooks';
import axios, { AxiosResponse } from 'axios';
import { z } from 'zod';

import { ApiError, api } from '@/lib/api-client.ts';

import { AvailabilityResponse, AvailableTimeslots } from '@/models/Availability.ts';
import {
    AvailabilityStatus,
    CollectionOccurrence,
    IntervalType,
    PreferredAvailability,
} from '@/models/appointment/Appointment.ts';
import type {
    ChainAutoSuggestionParams,
    ChainAvailabilityItem,
    ChainAvailabilityParams,
} from '@/models/appointment/AppointmentChainAvailability.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { InsuranceType } from '@/models/insurance.ts';

import { chainAvailabilityResponseSchema } from '@/schemas/chain-availability.schema.ts';

import { ServiceUrl } from '@/services/ServiceURL.ts';

const logger = getLogger(['service', 'availability']);

interface AvailableTimeslotsResponse {
    data: AvailabilityResponse;
}

const backendStatusMap: Record<string, AvailabilityStatus> = {
    AVAILABLE: AvailabilityStatus.AVAILABLE,
    SUGGESTION: AvailabilityStatus.PROPOSED,
    NOT_AVAILABLE: AvailabilityStatus.UNAVAILABLE,
};

const seriesAvailabilityItemSchema = z.object({
    start_date: z.string(),
    end_date: z.string(),
    status: z.string().transform((val) => backendStatusMap[val] ?? AvailabilityStatus.UNAVAILABLE),
});

const seriesAvailabilityResponseSchema = z.object({
    data: z.array(seriesAvailabilityItemSchema),
});

export interface SeriesAvailabilityParams {
    pvs_provider: PVSProvider;
    customer_id: string;
    user_id: string;
    location_key: string;
    appointment_type_key: string;
    appointment_type_id?: string;
    insurance_type: InsuranceType;
    pvs_calendar_id: string;
    is_online: boolean;
    is_returning_patient: boolean;
    start_date: string;
    interval_type: IntervalType;
    interval_frequency: number;
    number_of_appointments: number;
    preferred_availability: PreferredAvailability;
    preferred_time?: string;
    collection_id?: string;
}

export interface SeriesAutoSuggestionParams extends SeriesAvailabilityParams {
    appointment_type_id: string;
    start_date_anchor?: string;
}

type SeriesAvailabilityItem = z.infer<typeof seriesAvailabilityItemSchema>;

const mapSeriesResponse = (
    items: SeriesAvailabilityItem[],
    preferredAvailability: PreferredAvailability,
    preferredTime?: string
): CollectionOccurrence[] =>
    items.map((item) => ({
        id: randomId(),
        start_datetime: item.start_date,
        end_datetime: item.end_date,
        availability: item.status,
        original_time:
            preferredAvailability === PreferredAvailability.FIXED && item.status === AvailabilityStatus.PROPOSED
                ? preferredTime
                : undefined,
    }));

export const getAvailableTimeslots = async ({
    pvs_provider,
    customer_id,
    user_id,
    location_key,
    appointment_type_key,
    insurance_type,
    pvs_calendar_id,
    start_date,
    is_online = false,
    is_returning = false,
}: AvailableTimeslots): Promise<AvailabilityResponse> => {
    // TODO: add option in api wrapper to allow removal of auth header

    const params: Record<string, string> = {
        location_key: location_key,
        appointment_type_keys: appointment_type_key,
        insurance_type: insurance_type,
        start_date: start_date,
        is_online: is_online.toString(),
        is_returning: is_returning.toString(),
        journey_type: 'office-app-book',
    };

    switch (pvs_provider) {
        case PVSProvider.DOC_CIRRUS: {
            params.dc_calendar_id = pvs_calendar_id;
            break;
        }
        case PVSProvider.DATA_CUR: {
            params.data_cur_resource_id = pvs_calendar_id;
            break;
        }
        default: {
            logger.error('PVS provider: {pvsProvider} not supported', { pvsProvider: pvs_provider });
            break;
        }
    }

    const response: AxiosResponse<AvailableTimeslotsResponse> = await axios.get(
        `${ServiceUrl.PRACTITIONER_API_DOMAIN.PUBLIC.USERS.AVAILABILITY}/${user_id}`,
        {
            headers: {
                customer_id: customer_id,
            },
            params: params,
        }
    );

    return response.data.data;
};

export const getSeriesAvailability = async ({
    pvs_provider,
    customer_id,
    user_id,
    location_key,
    appointment_type_key,
    insurance_type,
    pvs_calendar_id,
    is_online,
    is_returning_patient,
    start_date,
    interval_type,
    interval_frequency,
    number_of_appointments,
    preferred_availability,
    preferred_time,
    collection_id,
}: SeriesAvailabilityParams): Promise<CollectionOccurrence[]> => {
    const params: Record<string, string> = {
        location_key: location_key,
        appointment_type_keys: appointment_type_key,
        insurance_type: insurance_type,
        is_online: is_online.toString(),
        is_returning: is_returning_patient.toString(),
        journey_type: 'office-app-book',
        start_date: start_date,
        interval_type: interval_type,
        interval_frequency: interval_frequency.toString(),
        number_of_appointments: number_of_appointments.toString(),
        preferred_availability: preferred_availability,
    };

    if (preferred_time) {
        params.preferred_time = preferred_time;
    }

    if (collection_id) {
        params.collection_id = collection_id;
    }

    switch (pvs_provider) {
        case PVSProvider.DOC_CIRRUS: {
            params.dc_calendar_id = pvs_calendar_id;
            break;
        }
        case PVSProvider.DATA_CUR: {
            params.data_cur_resource_id = pvs_calendar_id;
            break;
        }
        default: {
            logger.error('PVS provider: {pvsProvider} not supported', { pvsProvider: pvs_provider });
            break;
        }
    }

    const response: AxiosResponse = await axios.get(
        `${ServiceUrl.PRACTITIONER_API_DOMAIN.PUBLIC.USERS.AVAILABILITY}/${user_id}/series-availability`,
        {
            headers: {
                customer_id: customer_id,
            },
            params: params,
        }
    );

    const parsed = seriesAvailabilityResponseSchema.safeParse(response.data);

    if (!parsed.success) {
        throw new ApiError(502, 'Invalid series availability response', parsed.error);
    }

    return mapSeriesResponse(parsed.data.data, preferred_availability, preferred_time);
};

export const getSeriesAutoSuggestion = async ({
    pvs_provider,
    customer_id,
    user_id,
    appointment_type_id,
    insurance_type,
    pvs_calendar_id,
    is_returning_patient,
    interval_type,
    interval_frequency,
    number_of_appointments,
    preferred_availability,
    preferred_time,
    collection_id,
    start_date_anchor,
}: SeriesAutoSuggestionParams): Promise<CollectionOccurrence[]> => {
    if (!customer_id) {
        throw new ApiError(400, 'missing customer id');
    }

    const body: Record<string, unknown> = {
        appointment_type_id: appointment_type_id,
        insurance_type: insurance_type,
        is_returning: is_returning_patient,
        journey_type: 'office-app-book',
        professional_id: user_id,
        series_config: {
            interval_type: interval_type,
            interval_frequency: interval_frequency,
            number_of_appointments: number_of_appointments,
            preferred_availability: preferred_availability,
            ...(preferred_time ? { preferred_time: preferred_time } : {}),
        },
    };

    if (collection_id) {
        body.collection_id = collection_id;
    }

    if (start_date_anchor) {
        body.start_date = start_date_anchor;
    }

    switch (pvs_provider) {
        case PVSProvider.DOC_CIRRUS: {
            body.dc_calendar_id = pvs_calendar_id;
            break;
        }
        case PVSProvider.DATA_CUR: {
            body.data_cur_resource_id = pvs_calendar_id;
            break;
        }
        default: {
            throw new ApiError(400, `Unsupported PVS provider: ${pvs_provider}`);
        }
    }

    const response: AxiosResponse = await api.post(
        ServiceUrl.PRACTITIONER_API_DOMAIN.SECURE_ADMIN.USER.SERIES_AUTO_SUGGEST,
        body,
        {
            headers: {
                customer_id: customer_id,
            },
        }
    );

    const parsed = seriesAvailabilityResponseSchema.safeParse(response.data);

    if (!parsed.success) {
        throw new ApiError(502, 'Invalid series auto-suggestion response', parsed.error);
    }

    return mapSeriesResponse(parsed.data.data, preferred_availability, preferred_time);
};

export const getChainAvailability = async (params: ChainAvailabilityParams): Promise<ChainAvailabilityItem[]> => {
    const {
        customer_id,
        insurance_type,
        instance_id,
        chain_id,
        start_date,
        journey_type,
        steps,
        main_practitioner_id,
    } = params;

    if (!customer_id) {
        throw new ApiError(400, 'missing customer id');
    }

    const queryParams: Record<string, string> = {
        insurance_type: insurance_type,
        instance_id: instance_id,
        chain_id: chain_id,
        start_date: start_date,
        journey_type: journey_type,
    };

    if (main_practitioner_id) {
        queryParams.main_practitioner_id = main_practitioner_id;
    }

    const response: AxiosResponse = await axios.post(
        ServiceUrl.PRACTITIONER_API_DOMAIN.PUBLIC.USERS.CHAIN_AVAILABILITY,
        { steps },
        {
            headers: { customer_id: customer_id },
            params: queryParams,
        }
    );

    const parsed = chainAvailabilityResponseSchema.safeParse(response.data);
    if (!parsed.success) {
        throw new ApiError(502, 'Invalid chain availability response', parsed.error);
    }
    return parsed.data.data;
};

export const getChainAutoSuggestion = async (params: ChainAutoSuggestionParams): Promise<ChainAvailabilityItem[]> => {
    const {
        customer_id,
        insurance_type,
        is_returning,
        instance_id,
        chain_id,
        start_date,
        journey_type,
        steps,
        main_practitioner_id,
    } = params;

    if (!customer_id) {
        throw new ApiError(400, 'missing customer id');
    }

    const body: Record<string, unknown> = {
        insurance_type: insurance_type,
        is_returning: is_returning,
        instance_id: instance_id,
        chain_id: chain_id,
        journey_type: journey_type,
        start_date: start_date,
        steps: steps,
    };

    if (main_practitioner_id) {
        body.main_practitioner_id = main_practitioner_id;
    }

    const response: AxiosResponse = await axios.post(
        ServiceUrl.PRACTITIONER_API_DOMAIN.PUBLIC.USERS.CHAIN_AUTO_SUGGEST,
        body,
        {
            headers: { customer_id: customer_id },
        }
    );

    const parsed = chainAvailabilityResponseSchema.safeParse(response.data);
    if (!parsed.success) {
        throw new ApiError(502, 'Invalid chain auto-suggestion response', parsed.error);
    }
    return parsed.data.data;
};
